/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useState } from 'react';
import styled from 'styled-components';

import { object as YupObject } from 'yup';
import { Formik } from 'formik';

import Input from '@ntuctech/devex-tangram/Input';
import Button from '@ntuctech/devex-tangram/Button';
import Box from '@ntuctech/devex-tangram/Box';
import Typography from '@ntuctech/devex-tangram/Typography';
import Modal from '@ntuctech/devex-tangram/Modal';
import ModalTitle from '@ntuctech/devex-tangram/ModalTitle';
import ModalContent from '@ntuctech/devex-tangram/ModalContent';
import ModalActions from '@ntuctech/devex-tangram/ModalActions';
import InputValidator from '../../services/input-validator';
import GA from '../../services/google-analytics-tags';

import { useAuth0 } from '../Auth0Provider';
import Logger from '../../services/logger';
import Error from '../theme/Error';
import FormElement from '../theme/FormElement';
import FormActions from '../theme/FormActions';

import { usePageContext } from '../PageContextProvider';
import { PAGE_CONTEXT } from '../../constants/app-constants';

const MobileResolverSC = styled('div')`
  @media (max-width: ${(props) => `${parseInt(props.theme.SizeBreakpointsS, 10) - 1}px`}) {
    display: flex;
    justify-content: flex-end;
    width: 100%;

    & div {
      margin-bottom: 0;
    }

    & button {
      width: unset;
      margin-bottom: 0;
    }

    & .SSOFormAction {
      margin-top: 0.5rem;
      margin-bottom: 0;
      flex-direction: row-reverse;
    }

    & .SSOFormLastAction {
      margin-right: 0.75rem;
    }
  }
`;

function FormContent(props) {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    isValid,
    dirty,
    isSubmitting,
    submitForm,
    modalOpen,
    closeModal,
  } = props;

  const { email } = values;

  const showErrorFor = (name) => {
    return touched[name] && Boolean(errors[name]);
  };
  const showEmailError = showErrorFor('email');

  const isSubmitButtonDisabled = !dirty || !isValid || isSubmitting;

  const { setPageContext } = usePageContext();

  const handleCancel = () => {
    GA.track('Forgot Password', 'Navigate to Sign-in');
    setPageContext(PAGE_CONTEXT.LOGIN);
  };

  const handleFocus = React.useCallback((event) => {
    GA.track('Forgot Password', `Forgot Password Interacted`, event.target.name);
  }, []);

  const handleKeyPress = React.useCallback(
    (event) => {
      if (event.key === 'Enter' && !isSubmitButtonDisabled) {
        event.target.blur();
        submitForm();
      }
    },
    [submitForm, isSubmitButtonDisabled]
  );

  return (
    <>
      <Modal id="SSOPage__reset_link_sent" open={modalOpen}>
        <ModalTitle>Check your email</ModalTitle>
        <ModalContent>
          <Typography>We’ve sent you an email with instructions to reset your password.</Typography>
        </ModalContent>
        <ModalActions>
          <Button onClick={closeModal} variant="contained">
            Okay, got it
          </Button>
        </ModalActions>
      </Modal>

      <FormElement>
        <Input
          id="IdentityForgotPassword"
          type="email"
          name="email"
          label="Email address"
          helpText={showEmailError ? errors.email : ''}
          error={showEmailError}
          onChange={handleChange}
          onBlur={handleBlur}
          value={email}
          onFocus={handleFocus}
          required
          fullWidth
          autoComplete="new-email"
          onKeyPress={handleKeyPress}
        />
      </FormElement>

      <MobileResolverSC>
        <FormActions>
          <Box mb={1}>
            <Button
              variant="contained"
              compact
              disabled={isSubmitButtonDisabled}
              onClick={submitForm}
            >
              Send reset link
            </Button>
          </Box>
          <Box className="SSOFormLastAction" mb={-4}>
            <Button variant="contained" color="grey" compact onClick={handleCancel}>
              Cancel
            </Button>
          </Box>
        </FormActions>
      </MobileResolverSC>
    </>
  );
}

export default function ForgotForm() {
  const [open, setOpen] = React.useState(false);
  const openModal = React.useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = React.useCallback(() => {
    setOpen(false);
  }, []);

  const Auth0Instance = useAuth0();

  const [error, setError] = useState(null);

  const initialValues = {
    email: '',
  };

  const ForgotSchema = YupObject().shape({
    email: InputValidator.emailValidation({
      emptyEmailMessage: 'Email address is required.',
      invalidEmailMessage: 'Email address is not valid.',
      maxLengthMessage: 'Your email address should be within 64 characters.',
    }),
  });

  const onSubmit = (values, { resetForm, setSubmitting }) => {
    GA.track('Forgot Password', 'Forgot Password Request Initiated');
    Auth0Instance.forgotPassword(
      {
        ...values,
      },
      (err) => {
        setSubmitting(false);
        setError(err);
        if (err) {
          GA.track('Forgot Password', 'Forgot Password Request Failure');
          Logger.error(err);
          return;
        }
        GA.track('Forgot Password', 'Forgot Password Request Success');
        resetForm();
        openModal();
      }
    );
    return false;
  };

  return (
    <>
      <Error error={error} />
      <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={ForgotSchema}>
        {(formikProps) => <FormContent modalOpen={open} closeModal={closeModal} {...formikProps} />}
      </Formik>
    </>
  );
}
